import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardList, Filter, Plus, RotateCcw } from "lucide-react";

import { useLoanSummary, useLoans } from "@/api/queries";
import type { Loan } from "@/api/types";
import { Button } from "@/components/Button";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDate, formatDateTime, formatOverdue, pluralize } from "@/lib/format";
import { loanTone } from "@/lib/stock";
import { CheckOutDialog } from "./CheckOutDialog";
import { ReturnDialog } from "./ReturnDialog";

const PAGE_SIZE = 50;

type Scope = "active" | "overdue" | "all";

/**
 * Loans and returns (Frontend Spec §17, PHASE 8).
 *
 * The screen answers "qui a la tondeuse ?" — which tool, which person, since
 * when, due when. Overdue comes from the server: a phone with a wrong clock
 * must not disagree with the list everyone else is looking at.
 */
export function LoansPage() {
  const [params, setParams] = useSearchParams();
  const scope = (params.get("scope") as Scope | null) ?? "active";
  const offset = Number(params.get("offset") ?? 0);

  const [checkingOut, setCheckingOut] = useState(false);
  const [returning, setReturning] = useState<Loan | null>(null);

  const query = useMemo(
    () => ({
      status: scope === "all" ? undefined : ("ACTIVE" as const),
      overdue_only: scope === "overdue",
      limit: PAGE_SIZE,
      offset,
    }),
    [scope, offset],
  );

  const { data, isLoading, error, refetch } = useLoans(query);
  const { data: summary } = useLoanSummary();

  function update(next: Record<string, string | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === "") merged.delete(key);
      else merged.set(key, value);
    }
    if (!("offset" in next)) merged.delete("offset");
    setParams(merged, { replace: true });
  }

  return (
    <Page>
      <PageHeader
        title="Prêts / Retours"
        subtitle="Qui a quoi, depuis quand, et pour quand c'est promis."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCheckingOut(true)}>
            <Plus className="size-4" aria-hidden />
            Nouveau prêt
          </Button>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border p-3">
          <ScopeTab
            label="En cours"
            count={summary?.active}
            active={scope === "active"}
            onClick={() => update({ scope: undefined })}
          />
          <ScopeTab
            label="En retard"
            count={summary?.overdue}
            urgent
            active={scope === "overdue"}
            onClick={() => update({ scope: "overdue" })}
          />
          <ScopeTab
            label="Tout l'historique"
            active={scope === "all"}
            onClick={() => update({ scope: "all" })}
          />
          {summary && summary.due_soon > 0 && scope === "active" && (
            <span className="ml-auto text-[12px] text-ink-subtle">
              {pluralize(summary.due_soon, "retour")} prévu(s) d'ici 3 jours
            </span>
          )}
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={6} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={
                scope === "active" ? (
                  <ClipboardList className="size-6" aria-hidden />
                ) : (
                  <Filter className="size-6" aria-hidden />
                )
              }
              title={
                scope === "overdue"
                  ? "Rien en retard"
                  : scope === "all"
                    ? "Aucun prêt enregistré"
                    : "Aucun outil sorti"
              }
              description={
                scope === "overdue"
                  ? "Tout ce qui est sorti est encore dans les délais."
                  : "Prêtez un outil pour qu'il apparaisse ici, avec son emprunteur et son retour prévu."
              }
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="max-w-0">Outil</Th>
                      <Th className="max-w-0">Personne</Th>
                      <Th className="hidden whitespace-nowrap @4xl:table-cell">Sortie</Th>
                      <Th className="whitespace-nowrap">Retour prévu</Th>
                      <Th>Statut</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((loan) => (
                      <LoanRow key={loan.id} loan={loan} onReturn={() => setReturning(loan)} />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((loan) => (
                  <li key={loan.id}>
                    <LoanCard loan={loan} onReturn={() => setReturning(loan)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={(next) => update({ offset: String(next) })}
          />
        )}
      </div>

      <CheckOutDialog open={checkingOut} onOpenChange={setCheckingOut} />
      <ReturnDialog
        loan={returning}
        onOpenChange={(open) => {
          if (!open) setReturning(null);
        }}
      />
    </Page>
  );
}

function ScopeTab({
  label,
  count,
  active,
  urgent,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  urgent?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] transition-colors " +
        (active
          ? "bg-surface-raised font-medium text-ink"
          : "text-ink-muted hover:bg-surface-hover hover:text-ink")
      }
    >
      {label}
      {count !== undefined && count > 0 && (
        <span
          className={
            "tnum rounded px-1.5 text-[11px] " +
            (urgent ? "bg-danger-subtle text-status-out" : "bg-surface-sunken text-ink-subtle")
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ToolCell({ loan }: { loan: Loan }) {
  return (
    <Link to={`/assets/${loan.asset_id}`} className="block min-w-0 hover:text-brand">
      <span className="block truncate font-medium text-ink">
        {loan.asset_name ?? loan.product_name ?? loan.asset_code}
      </span>
      <span className="block truncate font-mono text-[11px] text-ink-subtle">
        {loan.asset_code}
        {loan.asset_serial_number ? ` · ${loan.asset_serial_number}` : ""}
      </span>
    </Link>
  );
}

function DueCell({ loan }: { loan: Loan }) {
  const late = formatOverdue(loan.days_overdue);
  if (!loan.due_on) {
    // "He has it until the job finishes" is a real answer; inventing a date
    // would put a fake deadline in the overdue list.
    return <span className="text-ink-subtle">Sans échéance</span>;
  }
  return (
    <span className={late ? "text-status-out" : "text-ink"}>
      {formatDate(loan.due_on)}
      {late && <span className="block text-[11px]">{late}</span>}
    </span>
  );
}

function LoanRow({ loan, onReturn }: { loan: Loan; onReturn: () => void }) {
  return (
    <Tr>
      <Td className="max-w-0">
        <ToolCell loan={loan} />
      </Td>
      <Td className="max-w-0">
        <span className="block truncate text-ink">{loan.person_name}</span>
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(loan.checked_out_at)}
      </Td>
      <Td className="whitespace-nowrap text-[13px]">
        <DueCell loan={loan} />
      </Td>
      <Td>
        <StatusBadge tone={loanTone(loan.status, loan.is_overdue)}>
          {loan.status === "RETURNED" ? "Rendu" : loan.is_overdue ? "En retard" : "Sorti"}
        </StatusBadge>
      </Td>
      <Td align="right">
        {loan.status === "ACTIVE" ? (
          <Button variant="secondary" size="sm" onClick={onReturn}>
            <RotateCcw className="size-4" aria-hidden />
            Retour
          </Button>
        ) : (
          <span className="whitespace-nowrap text-[12px] text-ink-subtle">
            {formatDateTime(loan.returned_at)}
          </span>
        )}
      </Td>
    </Tr>
  );
}

function LoanCard({ loan, onReturn }: { loan: Loan; onReturn: () => void }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="flex items-start justify-between gap-3">
        <ToolCell loan={loan} />
        <StatusBadge tone={loanTone(loan.status, loan.is_overdue)}>
          {loan.status === "RETURNED" ? "Rendu" : loan.is_overdue ? "En retard" : "Sorti"}
        </StatusBadge>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
        <div>
          <dt className="text-ink-subtle">Personne</dt>
          <dd className="truncate text-ink">{loan.person_name}</dd>
        </div>
        <div>
          <dt className="text-ink-subtle">Retour prévu</dt>
          <dd>
            <DueCell loan={loan} />
          </dd>
        </div>
      </dl>
      {loan.status === "ACTIVE" && (
        <Button variant="secondary" size="sm" onClick={onReturn} className="self-start">
          <RotateCcw className="size-4" aria-hidden />
          Enregistrer le retour
        </Button>
      )}
    </div>
  );
}
